import React, { useMemo } from 'react';
import {
  Chart,
  BarController,
  BarElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from 'chart.js';

import { Bar } from 'react-chartjs-2';
import ChartCard from './ChartCard';
import { useChartTheme } from './useChartTheme';
import { SEV_COLORS, CARD_SURFACE_LIGHT, CARD_SURFACE_DARK } from './constants';
import { stackTotalsPlugin } from './chartPlugins';
import {
  buildClientTimeline,
  formatShortDate,
  formatFullDate,
  PLOTTED_SEVERITIES,
} from './trajectoryTimeline';
import { ProjectRemediationSeries } from '../../Models/Retest';

Chart.register(BarController, BarElement, LinearScale, CategoryScale, Tooltip, Legend);

interface FindingsPerTestChartProps {
  series: ProjectRemediationSeries[];
}

/**
 * One stacked column per test, colour-coded by risk level.
 *
 * Tests genuinely are discrete events — nothing was measured between two of them — so bars are
 * more honest here than a continuous line, which would imply readings that do not exist. Each
 * column is labelled with what the event was, because a rise is only legible once you know a
 * project was delivered that day.
 */
const FindingsPerTestChart: React.FC<FindingsPerTestChartProps> = ({ series }) => {
  const { textColor, gridColor, isDark } = useChartTheme();
  // The gap between stacked segments is drawn in the surface colour, so it reads as space
  // rather than as a stroke around each block.
  const surface = isDark ? CARD_SURFACE_DARK : CARD_SURFACE_LIGHT;

  const timeline = useMemo(() => buildClientTimeline(series), [series]);

  const chartData = {
    labels: timeline.map(e => [formatShortDate(e.date), e.kind === 'delivery' ? 'delivered' : 'retest']),
    datasets: PLOTTED_SEVERITIES.map(({ key, label }) => ({
      label,
      data: timeline.map(e => e.counts[key] ?? 0),
      backgroundColor: SEV_COLORS[label],
      borderColor: surface,
      borderWidth: { top: 1, bottom: 1, left: 0, right: 0 },
      borderSkipped: false as const,
      borderRadius: 2,
      maxBarThickness: 46,
      stack: 'test',
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 22 } },
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { color: textColor, font: { size: 11 }, boxWidth: 11, boxHeight: 11 },
      },
      tooltip: {
        callbacks: {
          title: (items: any[]) => (timeline[items[0]?.dataIndex] ? formatFullDate(timeline[items[0].dataIndex].date) : ''),
          beforeBody: (items: any[]) => timeline[items[0]?.dataIndex]?.label ?? '',
          footer: (items: any[]) => `Total open  ${items.reduce((a, i) => a + Number(i.parsed.y ?? 0), 0)}`,
        },
      },
      stackTotals: { color: textColor },
    },
    scales: {
      x: {
        type: 'category' as const,
        stacked: true,
        grid: { display: false },
        ticks: { color: textColor, font: { size: 11 }, maxRotation: 0, autoSkipPadding: 8 },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: gridColor },
        ticks: { color: textColor, font: { size: 11 }, precision: 0 },
      },
    },
  };

  return (
    <ChartCard
      title="Open findings at each test"
      sub="All delivered projects · stacked by risk level"
    >
      {timeline.length === 0 ? (
        <div className="text-muted text-center py-5 small">
          No delivered projects to show a trajectory for yet
        </div>
      ) : (
        <div data-testid="findings-per-test-plot" style={{ height: 300, width: '100%' }}>
          <Bar data={chartData} options={options} plugins={[stackTotalsPlugin]} />
        </div>
      )}
    </ChartCard>
  );
};

export default FindingsPerTestChart;
